import { Fragment, useState, useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Lock } from 'lucide-react';
import { BADGE_TIERS, nextTier } from '../lib/badges';
import { useTheme } from '../context/ThemeContext';

/**
 * "Rank journey" trophy case for the profile — all five tier badges shown in a
 * themed box, linked by connectors that fill by their own percentage. Reached
 * tiers glow in their colour; the current tier pulses; locked tiers stay
 * visible (lightly greyed) with a small corner lock. Honors reduced-motion.
 */
export default function BadgeProgress({ count }: { count: number }) {
  const { isDark } = useTheme();
  const reduce = useReducedMotion();

  // Shrink the coins on small screens so all five tiers fit without clipping.
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const update = () => setNarrow(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const currentIdx = BADGE_TIERS.findIndex((t) => count >= t.min && count <= t.max);
  const next = nextTier(count);

  const COIN = narrow ? 42 : 60;

  // Fill % for the connector between tier i and i+1.
  const segPct = (i: number) => {
    const cur = BADGE_TIERS[i];
    const nxt = BADGE_TIERS[i + 1];
    if (!nxt) return 0;
    if (count >= nxt.min) return 100; // fully passed
    if (count >= cur.min) {
      // Partial: count the current order so an active tier never reads empty,
      // and never hits 100 until you actually advance.
      return ((count - cur.min + 1) / (nxt.min - cur.min + 1)) * 100;
    }
    return 0;
  };

  // Theme-aware surfaces
  const box = isDark
    ? { background: 'linear-gradient(150deg, #0c1322 0%, #070b12 100%)', border: '1px solid rgba(255,255,255,0.08)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05), 0 14px 34px rgba(0,0,0,0.35)' }
    : { background: 'linear-gradient(150deg, #ffffff 0%, #f1f5f9 100%)', border: '1px solid rgba(0,0,0,0.08)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.8), 0 10px 26px rgba(0,0,0,0.08)' };
  const railBg = isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)';
  const nameColor = (reached: boolean) => (reached ? (isDark ? '#e5e7eb' : '#1f2937') : (isDark ? '#4b5563' : '#9ca3af'));
  const rangeColor = (reached: boolean) => (reached ? (isDark ? '#6b7280' : '#6b7280') : (isDark ? '#374151' : '#cbd5e1'));

  return (
    <div className="mt-6 rounded-2xl p-4 pt-4 sm:p-6 sm:pt-5 text-left relative overflow-hidden" style={box}>
      {/* header */}
      <div className="flex items-center justify-between mb-5">
        <p className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Rank Journey</p>
        <span
          className="text-[11px] font-semibold px-2.5 py-1 rounded-full"
          style={{ background: 'rgba(16,185,129,0.12)', color: isDark ? '#34d399' : '#059669', border: '1px solid rgba(52,211,153,0.35)' }}
        >
          {next ? `${next.remaining} to ${next.tier.name}` : 'Max tier 🏆'}
        </span>
      </div>

      {/* badges + connectors */}
      <div className="flex items-center" style={{ paddingBottom: 48 }}>
        {BADGE_TIERS.map((t, i) => {
          const reached = count >= t.min;
          const isCurrent = i === currentIdx;
          const range = t.max === Infinity ? `${t.min}+` : `${t.min}–${t.max}`;
          return (
            <Fragment key={t.slug}>
              {/* coin + labels */}
              <div className="relative flex flex-col items-center shrink-0" style={{ width: COIN }}>
                {/* current-tier enclosure — matches the Compare-ranks column highlight */}
                {isCurrent && (
                  <div
                    className="absolute left-1/2 -translate-x-1/2 rounded-2xl pointer-events-none"
                    style={{ top: -12, width: COIN + 24, height: COIN + 72, background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(52,211,153,0.30)' }}
                  />
                )}
                <div
                  className="relative rounded-full"
                  style={{
                    width: COIN,
                    height: COIN,
                    background: 'radial-gradient(circle at 50% 42%, #0a0f0d, #04070a)',
                    boxShadow: reached ? `0 0 0 1.5px ${t.glow}, 0 0 16px -1px ${t.glow}` : `0 0 0 1px ${railBg}`,
                    transform: isCurrent ? 'scale(1.1)' : 'scale(1)',
                    transition: 'transform .4s, box-shadow .4s',
                    zIndex: isCurrent ? 2 : 1,
                  }}
                >
                  <img
                    src={t.src}
                    alt={`${t.name} badge`}
                    draggable={false}
                    className="absolute inset-0 w-full h-full rounded-full object-cover select-none"
                    style={{
                      mixBlendMode: 'screen',
                      transform: 'scale(1.02)',
                      filter: 'none',
                      opacity: 1,
                    }}
                  />
                  {/* small corner lock — keeps the badge art visible */}
                  {!reached && (
                    <div
                      className="absolute -bottom-0.5 -right-0.5 rounded-full flex items-center justify-center"
                      style={{ width: 18, height: 18, background: isDark ? '#1f2937' : '#374151', border: `1.5px solid ${isDark ? '#0c1322' : '#f1f5f9'}` }}
                    >
                      <Lock size={9} className="text-gray-300" />
                    </div>
                  )}
                  {/* pulsing ring on current tier */}
                  {isCurrent && !reduce && (
                    <motion.span
                      className="absolute inset-0 rounded-full"
                      style={{ boxShadow: `0 0 0 2px ${t.glow}` }}
                      animate={{ scale: [1, 1.5], opacity: [0.7, 0] }}
                      transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut' }}
                    />
                  )}
                </div>

                <div className="absolute top-full mt-2 flex flex-col items-center leading-tight">
                  <span className="text-[11px] font-bold whitespace-nowrap" style={{ color: nameColor(reached) }}>{t.name}</span>
                  <span className="text-[9px] font-medium whitespace-nowrap mt-0.5" style={{ color: rangeColor(reached) }}>{range}</span>
                  <span className="text-[9px] font-medium whitespace-nowrap" style={{ color: rangeColor(reached) }}>orders</span>
                </div>
              </div>

              {/* connector (fills by its own %) */}
              {i < BADGE_TIERS.length - 1 && (
                <div className="relative mx-1 sm:mx-1.5 h-[4px] rounded-full overflow-hidden" style={{ flex: '1 1 0', background: railBg }}>
                  <motion.div
                    className="absolute inset-y-0 left-0 rounded-full"
                    style={{
                      background: `linear-gradient(90deg, ${BADGE_TIERS[i].glow}, ${BADGE_TIERS[i + 1].glow})`,
                      boxShadow: `0 0 6px ${BADGE_TIERS[i].glow}`,
                    }}
                    initial={{ width: 0 }}
                    animate={{ width: `${segPct(i)}%` }}
                    transition={reduce ? { duration: 0 } : { duration: 0.9, ease: 'easeOut', delay: 0.12 * i }}
                  />
                </div>
              )}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
